import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { List } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { default as DesignSystemSidepanel } from 'modules/core/components/Sidepanel'
import Upgrade from 'modules/core/components/Upgrade'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { featureToRequest } from 'modules/reports/components/DashboardShow/components/upsellUtils'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface Props {
  open: boolean
  onClose: () => void
  dashboardId: number
}

export interface Option {
  title: string
  description: string
  showUpsellTag?: boolean
  eventToTrack: string
  to: string
}

const HeaderSidepanel = ({ open, onClose, dashboardId }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { track } = useReportTracking()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { featureAvailability } = useFeatureAvailability(Features.analytics_advanced_reports)
  const showUpsellTag = !featureAvailability.available
  const requestProps = featureToRequest()

  const options: Option[] = [
    {
      to: navigation.reports.dashboards.dashboardId.new.fullPath({ dashboard_id: dashboardId }),
      title: i18n.t('custom_reports.header.options.custom_report'),
      description: i18n.t('custom_reports.header.options.custom_report_description'),
      eventToTrack: trackingEvents.reports.startReportFromGenerator,
      showUpsellTag,
    },
    {
      to: navigation.reports.dashboards.dashboardId.sql.fullPath({ dashboard_id: dashboardId }),
      title: i18n.t('custom_reports.header.options.sql_report'),
      description: i18n.t('custom_reports.header.options.sql_report_description'),
      eventToTrack: trackingEvents.reports.startReportFromSql,
      showUpsellTag,
    },
    {
      to: navigation.reports.dashboards.dashboardId.list.question.fullPath({
        dashboard_id: dashboardId,
      }),
      title: i18n.t('custom_reports.header.options.question_report'),
      description: i18n.t('custom_reports.header.options.question_report_description'),
      eventToTrack: trackingEvents.reports.startReportFromQuestion,
      showUpsellTag,
    },
    {
      to: navigation.reports.dashboards.dashboardId.templates.fullPath({
        dashboard_id: dashboardId,
      }),
      title: i18n.t('custom_reports.header.options.report_library'),
      description: i18n.t('custom_reports.header.options.report_library_description'),
      eventToTrack: trackingEvents.reports.startReportFromLibrary,
    },
  ]

  return (
    <>
      <DesignSystemSidepanel
        open={open}
        onClose={() => {
          onClose()
        }}
      >
        <List>
          {options.map((option) => (
            <List.ClickableItem
              key={option.title}
              navigateTo={() => {
                onClose()

                if (option.showUpsellTag) {
                  setIsModalOpen(true)
                  return
                }

                track(option.eventToTrack)
                navigate(option.to)
              }}
            >
              <List.Title>{option.title}</List.Title>
              <List.Description>{option.description}</List.Description>
              {option.showUpsellTag && <Upgrade.Tiny />}
            </List.ClickableItem>
          ))}
        </List>
      </DesignSystemSidepanel>
      {isModalOpen && requestProps.moduleRequested && (
        <UpsellingKit.FeatureModal
          moduleRequested={requestProps.moduleRequested}
          requestSource={requestProps.requestSource ?? 'Upsell modal with feature info'}
          requestReason={requestProps.requestReason}
          featureRequested={Features.analytics_advanced_reports}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  )
}

export default HeaderSidepanel
